import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, TextField, Stack } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { generateAIChatResponse } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";

import {
  Container,
  HeaderContainer,
  InputContainer,
  MessageContainer,
  MessageContent,
} from "./styles";
import parse from "html-react-parser";
import { useDispatch, useSelector } from "react-redux";
import {
  setChatMessages,
  setConversationId,
  updateLastAIMessage,
} from "../../../redux/reducers/aiChat";
import DotLoader from "./dotloader";

const AiChat = () => {
  const { access_token, selected_practice, first_name, last_name } =
    useUserData();

  const [inputMessage, setInputMessage] = useState("");
  const dispatch = useDispatch();
  const chatMessages = useSelector((state) => state.aiChat.chatMessages);
  console.log("chatMessages: ", chatMessages);
  const conversationId = useSelector((state) => state.aiChat.conversationId);

  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const clearChat = () => {
    dispatch(setChatMessages([]));
    setInputMessage("");
    dispatch(setConversationId(null));
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    if (!conversationId) {
      dispatch(setConversationId(Date.now()));
    }

    const userMessage = { role: "user", content: inputMessage.trim() };

    const updatedMessages = [
      ...chatMessages,
      userMessage,
      { role: "ai", content: "", loading: true },
    ];
    dispatch(setChatMessages(updatedMessages));
    setInputMessage("");

    const form = new FormData();
    form.append("promptMessage", userMessage.content);
    form.append("practice_id", selected_practice);
    form.append("user_id", `${first_name} ${last_name}`);
    form.append("html_friendly", true);
    form.append(
      "conversation_id",
      conversationId ? conversationId : Date.now()
    );

    try {
      await generateAIChatResponse(form, access_token, (chunk) => {
        let clean = chunk.response
          .replace(/<!DOCTYPE[^>]*>/gi, "")
          .replace(/<\/?html[^>]*>/gi, "")
          .replace(/<\/?head[^>]*>/gi, "")
          .replace(/<\/?body[^>]*>/gi, "")
          .replace(/<meta[^>]*>/gi, "")
          .replace(/<title[^>]*>.*?<\/title>/gi, "");
        dispatch(updateLastAIMessage(clean));
      });
    } catch (err) {
      console.error("Stream error:", err);
      dispatch(updateLastAIMessage("⚠️ Error fetching response."));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Container>
      <HeaderContainer>
        <Typography variant="h6" fontWeight={700}>
          AI Chat
        </Typography>
        {chatMessages.length > 0 && (
          <Button variant="text" color="secondary" onClick={clearChat}>
            Clear Chat
          </Button>
        )}
      </HeaderContainer>
      <MessageContainer>
        {chatMessages.map((msg, i) => (
          <Stack
            key={i}
            direction="row"
            justifyContent={msg.role === "user" ? "flex-end" : "flex-start"}
            spacing={1}
          >
            <MessageContent
              elevation={2}
              sx={{
                backgroundColor: msg.role === "user" ? "#1976d2" : "#e0e0e0",
                color: msg.role === "user" ? "white" : "black",
              }}
            >
              {msg.role !== "ai" ? (
                msg.content
              ) : msg.loading ? (
                <DotLoader />
              ) : (
                parse(msg.content)
              )}
            </MessageContent>
          </Stack>
        ))}
        <div ref={messagesEndRef} />
      </MessageContainer>
      <InputContainer>
        <TextField
          placeholder="Type your message..."
          multiline
          maxRows={4}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          sx={{ mr: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={sendMessage}
          disabled={!inputMessage.trim()}
          endIcon={<SendIcon />}
        >
          Send
        </Button>
      </InputContainer>
    </Container>
  );
};

export default AiChat;
